<script setup lang="ts">
/**
 * Filter drawer for the investor wallets directory.
 *
 * Slides in from the right. Carries the investor name / email / KYC
 * status / balance range / currency filters. No status field for the
 * directory itself (it is not a queue). Pinned "Apply filters" CTA at
 * the bottom per `.claude/rules/ui-kit-theme.md → Drawer chrome for
 * form drawers`.
 *
 * Filter values are owned as a draft inside this component until the
 * operator hits Apply. The header "Reset" link clears every field at
 * once and emits `reset`.
 */
import { reactive, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Button from '@uiKit/elements/Button.vue'
import AppDrawer from '@uiKit/elements/Drawer.vue'
import Input from '@uiKit/elements/Input.vue'
import Select from '@uiKit/elements/Select.vue'
import DrawerHeader from '@uiKit/components/DrawerHeader.vue'
import FormField from '@uiKit/components/FormField.vue'
import { KYC_STATUS_OPTIONS } from '../utils/walletsDirectoryFormatters'

export interface WalletsDirectoryFilterValues {
  investorName: string
  investorEmail: string
  kycStatus: string | null
  minBalance: string
  maxBalance: string
  currencyCode: string
}

interface Props {
  visible: boolean
  values: WalletsDirectoryFilterValues
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', value: boolean): void
  (e: 'apply', values: WalletsDirectoryFilterValues): void
  (e: 'reset'): void
}>()

const draft = reactive<WalletsDirectoryFilterValues>({
  investorName: props.values.investorName,
  investorEmail: props.values.investorEmail,
  kycStatus: props.values.kycStatus,
  minBalance: props.values.minBalance,
  maxBalance: props.values.maxBalance,
  currencyCode: props.values.currencyCode,
})

watch(
  () => props.visible,
  (isVisible) => {
    if (!isVisible) return
    draft.investorName = props.values.investorName
    draft.investorEmail = props.values.investorEmail
    draft.kycStatus = props.values.kycStatus
    draft.minBalance = props.values.minBalance
    draft.maxBalance = props.values.maxBalance
    draft.currencyCode = props.values.currencyCode
  },
)

function handleApply() {
  emit('apply', {
    investorName: draft.investorName.trim(),
    investorEmail: draft.investorEmail.trim(),
    kycStatus: draft.kycStatus,
    minBalance: draft.minBalance.trim(),
    maxBalance: draft.maxBalance.trim(),
    currencyCode: draft.currencyCode.trim(),
  })
  emit('update:visible', false)
}

function handleReset() {
  draft.investorName = ''
  draft.investorEmail = ''
  draft.kycStatus = null
  draft.minBalance = ''
  draft.maxBalance = ''
  draft.currencyCode = 'AED'
  emit('reset')
}

function handleClose() {
  emit('update:visible', false)
}
</script>

<template>
  <AppDrawer
    :model-value="visible"
    position="right"
    width="440px"
    :show-close-icon="false"
    @update:model-value="(v) => !v && handleClose()"
  >
    <template #header>
      <DrawerHeader title="Filter wallets" subtitle="Refine the directory" @close="handleClose">
        <template #extras>
          <Button label="Reset" variant="text" color="secondary" @click="handleReset" />
        </template>
      </DrawerHeader>
    </template>

    <Box flex direction="col" class="drawer-body">
      <Box flex direction="col" gap="4" class="drawer-body__content">
        <FormField label="Investor name">
          <Input v-model="draft.investorName" placeholder="Name contains…" />
        </FormField>
        <FormField label="Investor email">
          <Input v-model="draft.investorEmail" placeholder="Email contains…" />
        </FormField>
        <FormField label="KYC status">
          <Select
            v-model="draft.kycStatus"
            :options="[...KYC_STATUS_OPTIONS]"
            option-label="label"
            option-value="value"
            placeholder="Any status"
            show-clear
          />
        </FormField>
        <FormField label="Currency">
          <Input v-model="draft.currencyCode" placeholder="AED" />
        </FormField>
        <FormField label="Min balance">
          <Input v-model="draft.minBalance" placeholder="0.00" />
        </FormField>
        <FormField label="Max balance">
          <Input v-model="draft.maxBalance" placeholder="100000.00" />
        </FormField>
      </Box>

      <Box class="drawer-body__actions">
        <Button label="Apply filters" color="primary" block size="large" @click="handleApply" />
      </Box>
    </Box>
  </AppDrawer>
</template>

<style scoped>
.drawer-body {
  min-height: 100%;
  padding: 1.25rem 1.5rem;
}

.drawer-body__content {
  flex: 1;
}

.drawer-body__actions {
  margin-top: auto;
  padding-top: 1rem;
}

@media (max-width: 992px) {
  .drawer-body {
    padding: 1rem;
  }
}
</style>
